import { setValidationErrors } from 'actions/errors';
import {
  SENDER_ADDRESS_FIELD,
  SENDER_COUNTRY_FIELD,
  WHITELISTED_COUNTRIES_FOR_PO_BOX,
} from 'constants/index';
import type { Field } from 'models/field';
import { getCurrentLocale, getFieldById, getFieldValue } from 'selectors';
import type { PayexThunkAction } from 'store/configureStore';
import { clientValidate } from 'utils/clientValidate/clientValidate';
import { constraints } from 'utils/clientValidate/constraints';
import { getI18nInstance } from 'utils/translations';

const generateConstraints = ({
  locale,
  fieldOrFields,
}: {
  locale: string;
  fieldOrFields: Field | Field[];
}) => {
  const i18n = getI18nInstance(locale);
  return constraints.generate({
    fieldOrFields,
    invalidMsg: i18n.t('form.errors.invalid') as string,
    requiredMsg: i18n.t('form.errors.required') as string,
  });
};

export const validateSectionField =
  (
    id: string,
    {
      displayValidationErrors = true,
    }: { displayValidationErrors?: boolean } | undefined = {},
  ): PayexThunkAction =>
  async (dispatch, getState) => {
    const state = getState();
    const field = getFieldById(state, id);
    const locale = getCurrentLocale(getState());
    const fieldConstraints = generateConstraints({
      locale,
      fieldOrFields: field,
    });

    try {
      await clientValidate.async(
        { [id]: field.defaultValue },
        fieldConstraints,
      );

      dispatch(
        setValidationErrors({ [id]: [] }, undefined, displayValidationErrors),
      );
      return true;
    } catch (errors) {
      dispatch(
        setValidationErrors(
          errors as Record<string, string[]>,
          undefined,
          displayValidationErrors,
        ),
      );
      return false;
    }
  };

export const validateSenderAddress =
  (): PayexThunkAction => async (dispatch, getState) => {
    const state = getState();
    const value = getFieldValue(state, SENDER_ADDRESS_FIELD) as string;
    const country = getFieldValue(state, SENDER_COUNTRY_FIELD) as string;
    const locale = getCurrentLocale(state);
    const i18n = getI18nInstance(locale);

    if (isPoBox(value) && !isCountryWhitelisted(country)) {
      const errors = {
        [SENDER_ADDRESS_FIELD]: [
          i18n.t('errors.po_box_not_accepted') as string,
        ],
      };
      dispatch(setValidationErrors(errors));
      return false;
    }

    return true;
  };

const isCountryWhitelisted = (country: string) => {
  return WHITELISTED_COUNTRIES_FOR_PO_BOX.includes(country);
};

const isPoBox = (address: string) => {
  const poBoxPattern =
    /^(((\d*\s?)?((p[. ]?o[. ]?)|(unit\s*\d*\s*box)|(post office box)|(office\s?box)|(p[. ]?o[. ]?\s?box)|(box)|(p[. ]?o[. ]?b[. ]?))(\s?\d*)?))(($)|(\s.*$))/gim;
  return poBoxPattern.test(address);
};
